// src/components/Eventos/VerticalEvents.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  TextField,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEventContext } from '../../context/EventContext'; 
import { computeGlobalPlaces } from '../../utils/globalRankings';
import { calculateBestMarkVertical, calculateVerticalTieBreakData } from '../../utils/ResultUtils';

// DnD Kit
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

// Componentes compartidos
import LaneSortableItem from './LaneSortableItem';
import ActionColumn, { ActionColumnHeader } from './ActionColumn';
import ResultModal from './ResultModal';

const VerticalEvents = ({ heatDetails }) => {
  const theme = useTheme();
  const { 
    updateHeatParticipants, 
    getTestSettings, 
    getParticipantResults,
    participantsResults: contextParticipantsResults, 
    saveSeedMarks,
    getSeedMarks,
    getPlace,
    hasPassed,
    updateParticipantResults,
    calculateRankings,
    pplData,
    heatHeights, updateHeatHeights,
    keysToDelete,
  } = useEventContext();

  // Detección de tamaño de pantalla
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // <900px
  
  // Estilos responsivos
  const responsiveStyles = useMemo(() => ({
    tableCell: {
      padding: isMobile ? '4px 2px' : isTablet ? '6px 4px' : '16px 8px',
      fontSize: isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.875rem',
    },
    button: {
      minWidth: isMobile ? '40px' : isTablet ? '60px' : '80px',
      padding: isMobile ? '2px 4px' : isTablet ? '4px 8px' : '8px 16px',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.75rem' : '0.875rem',
    },
    inputField: {
      width: isMobile ? '60px' : isTablet ? '80px' : '120px',
      '& .MuiInputBase-input': {
        fontSize: isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.875rem',
        padding: isMobile ? '6px 8px' : isTablet ? '8px 10px' : '10px 12px',
      }
    },
    typography: {
      nameSize: isMobile ? '0.7rem' : isTablet ? '0.8rem' : '0.875rem',
      teamSize: isMobile ? '0.6rem' : isTablet ? '0.65rem' : '0.75rem'
    },
    iconButton: {
      padding: isMobile ? '2px' : isTablet ? '4px' : '8px',
    },
    iconSize: isMobile ? '0.8rem' : isTablet ? '1rem' : '1.2rem',
    modalWidth: isMobile ? '90%' : isTablet ? '500px' : '600px',
    actionButtons: {
      width: isMobile ? '45px' : isTablet ? '60px' : '70px',
    }
  }), [isMobile, isTablet]);

  // Función para asegurarse de que un valor es un array
  const safeArray = (arr) => Array.isArray(arr) ? arr : [];

  // Información del evento
  const { eventCode, eventNumber, round, heat } = heatDetails;
  const { enablePoints } = getTestSettings(eventNumber, round) || { enablePoints: false };
  const heatKey = `${eventNumber}_${round}_${heat}`;

  const [isModifyingHeights, setIsModifyingHeights] = useState(false);
  // *** ESTADO LOCAL PARTICIPANTSRESULTS DEFINIDO AQUÍ ***
  const [participantsResults, setParticipantsResults] = useState({});
  const heights = heatHeights[heatKey] || [];
  const [openHeightModal, setOpenHeightModal] = useState(false);
  const [currentHeight, setCurrentHeight] = useState(0);
  const [selectedHeight, setSelectedHeight] = useState(null);
  const [isEditingHeight, setIsEditingHeight] = useState(false);
  const [openResultModal, setOpenResultModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [currentTrialIndex, setCurrentTrialIndex] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [tiebreakParticipants, setTiebreakParticipants] = useState([]);
  const [openTiebreakModal, setOpenTiebreakModal] = useState(false);
  const [tiebreakHeight, setTiebreakHeight] = useState(null);
  const [rankingsCalculated, setRankingsCalculated] = useState(false);
  const [seedMarks, setSeedMarks] = useState({});
  const [openSeedMarkModal, setOpenSeedMarkModal] = useState(false);
  const [selectedSeedParticipant, setSelectedSeedParticipant] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  // Estado para almacenar los participantes (como en los otros componentes)
  const [lanes, setLanes] = useState([]);
  // Para controlar el número de carriles
  const [lanesCount, setLanesCount] = useState(0);

  const initialHeight = heatDetails?.testSettings?.initialHeight || 0.0;
  const increment = heatDetails?.testSettings?.increment || 0.05;

  const isModifyingHeightsRef = useRef(false);
  const modificationTimeoutRef = useRef(null);

  // *** FUNCIONES HELPER MOVIDAS DESPUÉS DE LA DECLARACIÓN DEL ESTADO ***
  
  // Función helper para manejar la bandera de modificación
  const setModificationFlag = useCallback((value, duration = 2000) => {
    isModifyingHeightsRef.current = value;
    
    if (modificationTimeoutRef.current) {
      clearTimeout(modificationTimeoutRef.current);
    }
    
    if (value) {
      console.log('🔒 Bloqueando listeners por', duration, 'ms');
      modificationTimeoutRef.current = setTimeout(() => {
        isModifyingHeightsRef.current = false;
        console.log('🔓 Listeners desbloqueados');
      }, duration);
    }
  }, []);

  // Función helper para obtener resultados de altura de forma segura
  const getHeightResult = useCallback((participantId, heightKey, isTiebreak) => {
    const participant = participantsResults[participantId];
    if (!participant || !participant.heights || typeof participant.heights !== 'object') {
      return {
        attempts: isTiebreak ? [''] : ['', '', ''],
      };
    }
    
    const heightResult = participant.heights[heightKey];
    if (!heightResult || !heightResult.attempts) {
      return {
        attempts: isTiebreak ? [''] : ['', '', ''],
      };
    }
    
    return heightResult;
  }, [participantsResults]);

  // Función helper para verificar si un participante tiene datos válidos
  const hasValidParticipantData = useCallback((participantId) => {
    const participant = participantsResults[participantId];
    return participant && 
           typeof participant === 'object' && 
           participant.heights && 
           typeof participant.heights === 'object';
  }, [participantsResults]);

  // Participantes planos
  const participantsArray = Array.isArray(heatDetails.participants)
    ? heatDetails.participants
    : [];

  // Calcular lugares globales a través de todos los heats
  const globalPlaces = useMemo(() =>
    computeGlobalPlaces(
      contextParticipantsResults, 
      heatDetails.eventNumber, 
      heatDetails.round,
      heatDetails.eventCode,
      heatHeights // ← AGREGAR: Pasar todas las alturas de todos los heats
    ),
    [contextParticipantsResults, heatDetails.eventNumber, heatDetails.round, heatDetails.eventCode, heatHeights]
  );

  // Cálculo dinámico de carriles
  useEffect(() => {
    const numericLanes = participantsArray
      .map(p => parseInt(p.lane, 10))
      .filter(n => !isNaN(n));
    const maxAssigned = numericLanes.length ? Math.max(...numericLanes) : 0;
    const count = Math.max(maxAssigned, participantsArray.length);
    setLanesCount(count);
  }, [participantsArray]);

  // Cargar marcas de siembra
  useEffect(() => {
    const loadSeedMarks = async () => {
      const marks = {};
      for (const participant of participantsArray) {
        if (participant.idNumber) {
          try {
            const seedMarkData = await getSeedMarks(participant.idNumber);
            if (seedMarkData && seedMarkData[eventCode]?.seedMark) {
              marks[participant.idNumber] = seedMarkData[eventCode].seedMark;
            }
          } catch (error) {
            console.error(`Error cargando marca de siembra para ${participant.idNumber}:`, error);
          }
        }
      }
      setSeedMarks(marks);
    };
    
    if (participantsArray.length > 0) {
      loadSeedMarks();
    }
  }, [participantsArray, eventCode, getSeedMarks]);

  // Sincronizar datos de Firestore y del contexto
  useEffect(() => {
    if (participantsArray.length === 0) return;
    
    // Obtener resultados del contexto global (que incluye datos de Firestore)
    const heatKey = `${heatDetails.eventNumber}_${heatDetails.round}_${heatDetails.heat}`;
    const contextHeatResults = contextParticipantsResults[heatKey] || {};
    
    // Si hay resultados en el contexto, usarlos
    if (Object.keys(contextHeatResults).length > 0) {
      const updatedResults = {};
      
      participantsArray.forEach(participant => {
        const participantResults = contextHeatResults[participant.idNumber];
        if (participantResults) {
          updatedResults[participant.idNumber] = participantResults;
        }
      });
      
      if (Object.keys(updatedResults).length > 0) {
        setParticipantsResults(prevResults => ({
          ...prevResults,
          ...updatedResults
        }));
      }
    }
  }, [participantsArray, heatDetails, contextParticipantsResults]);

  // Escuchar cambios en tiempo real del contexto con control de modificación
  useEffect(() => {
    const heatKey = `${heatDetails.eventNumber}_${heatDetails.round}_${heatDetails.heat}`;
    const contextHeatResults = contextParticipantsResults[heatKey];
    
    // USAR REF en lugar de estado para verificar inmediatamente
    if (isModifyingHeightsRef.current) {
      console.log('🚫 Listener bloqueado - modificando alturas');
      return;
    }
    
    if (contextHeatResults) {
      // Debounce para evitar múltiples actualizaciones rápidas
      const timeoutId = setTimeout(() => {
        if (isModifyingHeightsRef.current) return; // Verificar nuevamente después del delay
        
        // Actualizar resultados de participantes
        setParticipantsResults(prev => {
          const updated = { ...prev };
          Object.entries(contextHeatResults).forEach(([participantId, results]) => {
            if (JSON.stringify(updated[participantId]) !== JSON.stringify(results)) {
              updated[participantId] = results;
            }
          });
          return updated;
        });
        
        // Reconstruir alturas solo si no estamos modificando
        const allHeights = new Set();
        Object.values(contextHeatResults).forEach(participantResult => {
          if (participantResult.heights) {
            Object.keys(participantResult.heights).forEach(heightKey => {
              const heightValue = parseFloat(heightKey);
              if (!isNaN(heightValue)) {
                const attempts = participantResult.heights[heightKey]?.attempts || [];
                const isTiebreak = attempts.length === 1;
                allHeights.add(JSON.stringify({ value: heightValue, isTiebreak }));
              }
            });
          }
        });
        
        if (allHeights.size > 0) {
          const reconstructedHeights = Array.from(allHeights)
            .map(h => JSON.parse(h))
            .sort((a, b) => {
              if (a.value !== b.value) return a.value - b.value;
              return a.isTiebreak ? 1 : -1;
            });
          
          if (JSON.stringify(reconstructedHeights) !== JSON.stringify(heights)) {
            console.log('🔄 Actualizando alturas desde listener (debounced)');
            updateHeatHeights(heatKey, reconstructedHeights);
          }
        }
      }, 300); // Debounce de 300ms
      
      return () => clearTimeout(timeoutId);
    }
  }, [contextParticipantsResults, heatDetails.eventNumber, heatDetails.round, heatDetails.heat, heights, updateHeatHeights]);

  // Efectos para inicializar alturas y sincronizar con Firestore
  useEffect(() => {
    // Primero intentar reconstruir alturas desde los datos guardados
    const reconstructHeightsFromResults = () => {
      const allHeights = new Set();
      
      // Recopilar todas las alturas únicas de todos los participantes
      participantsArray.forEach(participant => {
        const results = getParticipantResults(
          heatDetails.eventNumber,
          heatDetails.round,
          heatDetails.heat,
          participant.idNumber
        );
        
        if (results && results.heights) {
          Object.keys(results.heights).forEach(heightKey => {
            const heightValue = parseFloat(heightKey);
            if (!isNaN(heightValue)) {
              // Detectar si es una altura de desempate basándose en el número de intentos
              const attempts = results.heights[heightKey]?.attempts || [];
              const isTiebreak = attempts.length === 1;
              allHeights.add(JSON.stringify({ value: heightValue, isTiebreak }));
            }
          });
        }
      });
      
      // Si encontramos alturas guardadas, usarlas
      if (allHeights.size > 0) {
        const reconstructedHeights = Array.from(allHeights)
          .map(h => JSON.parse(h))
          .sort((a, b) => {
            // Ordenar por valor, con alturas de desempate al final
            if (a.value !== b.value) return a.value - b.value;
            return a.isTiebreak ? 1 : -1;
          });
        
        console.log('Alturas reconstruidas desde Firestore:', reconstructedHeights);
        return reconstructedHeights;
      }
      
      return null;
    };

    // Intentar reconstruir alturas desde datos existentes
    const existingHeights = reconstructHeightsFromResults();
    
    if (existingHeights && existingHeights.length > 0) {
      // Usar las alturas existentes de Firestore
      updateHeatHeights(heatKey, existingHeights);
      
      // Sincronizar el estado local con los datos de Firestore
      setParticipantsResults((prevResults) => {
        const updatedResults = { ...prevResults };
        participantsArray.forEach((participant) => {
          const existingResults = getParticipantResults(
            heatDetails.eventNumber,
            heatDetails.round,
            heatDetails.heat,
            participant.idNumber
          );
          
          if (existingResults) {
            updatedResults[participant.idNumber] = existingResults;
          }
        });
        return updatedResults;
      });
    } else if (heights.length === 0) {
      // Si no hay datos previos, inicializar con las alturas configuradas
      const numberOfHeights = heatDetails.testSettings?.numberOfHeights || 0;
      const calculatedHeights = Array.from({ length: numberOfHeights }, (_, i) => ({
        value: parseFloat((initialHeight + i * increment).toFixed(2)),
        isTiebreak: false,
      }));
      updateHeatHeights(heatKey, calculatedHeights);

      setParticipantsResults((prevResults) => {
        const updatedResults = { ...prevResults };
        (heatDetails.participants || []).forEach((participant) => {
          if (!updatedResults[participant.idNumber]) {
            // Intentar obtener resultados existentes
            const existingResults = getParticipantResults(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              participant.idNumber
            );
            
            if (existingResults && Object.keys(existingResults).length > 0) {
              updatedResults[participant.idNumber] = existingResults;
            } else {
              // Inicializar con datos vacíos
              updatedResults[participant.idNumber] = {
                specialStatus: null,
                heights: {},
                notes: '',
                points: 0
              };
              
              // Inicializar alturas para cada participante
              calculatedHeights.forEach((height) => {
                updatedResults[participant.idNumber].heights[height.value.toFixed(2)] = {
                  attempts: height.isTiebreak ? [''] : ['', '', ''],
                };
              });
            }
          }
        });
        return updatedResults;
      });
    }
  }, [heatDetails, initialHeight, increment, getParticipantResults, participantsArray, heights.length, heatKey, updateHeatHeights]);

  // Inicializar lanes
  useEffect(() => {
    if (participantsArray.length > 0) {
      const newLanes = Array(lanesCount).fill(null);
      let fillIndex = 0;

      participantsArray.forEach((athlete) => {
        const laneNumber = parseInt(athlete.lane, 10);
        if (!isNaN(laneNumber) && laneNumber > 0 && laneNumber <= lanesCount) {
          newLanes[laneNumber - 1] = athlete;
        } else {
          while (fillIndex < lanesCount && newLanes[fillIndex] !== null) {
            fillIndex++;
          }
          if (fillIndex < lanesCount) {
            newLanes[fillIndex] = athlete;
            fillIndex++;
          }
        }
      });

      setLanes(newLanes);
    } else {
      setLanes(Array(lanesCount).fill(null));
    }
  }, [participantsArray, lanesCount]);

  // Función para obtener la última altura franqueada con 'O' - VERSIÓN CORREGIDA
  const getLastHeightCleared = useCallback((participantId) => {
    if (!hasValidParticipantData(participantId)) return null;
    
    const participant = participantsResults[participantId];
    const clearedHeights = (heights || [])
      .filter((height) => {
        const heightKey = height.value.toFixed(2);
        const heightResult = participant.heights[heightKey];
        if (!heightResult || !heightResult.attempts) return false;
        
        const attempts = safeArray(heightResult.attempts);
        return attempts.includes('O');
      })
      .map((height) => height.value);
      
    if (clearedHeights.length === 0) return null;
    return Math.max(...clearedHeights);
  }, [hasValidParticipantData, participantsResults, heights, safeArray]);

  // Función para obtener el total de intentos fallidos ('X') hasta la última altura franqueada - VERSIÓN CORREGIDA
  const getTotalFailedAttempts = useCallback((participantId) => {
    if (!hasValidParticipantData(participantId)) return 0;
    
    const participant = participantsResults[participantId];
    const lastCleared = getLastHeightCleared(participantId);
    if (!lastCleared) return 0;
    
    let totalFailed = 0;
    const heightIndex = (heights || []).findIndex(h => h.value === lastCleared);
    
    const processHeight = (h, lastCleared) => {
      const hKey = h.value.toFixed(2);
      const heightResult = participant.heights[hKey];
      if (!heightResult || !heightResult.attempts) return;
      
      const results = safeArray(heightResult.attempts);
      results.forEach(res => {
        if (h.value < lastCleared) {
          if (res === 'X') totalFailed++;
        } else if (h.value === lastCleared) {
          if (h.isTiebreak) {
            if (res === 'X') totalFailed++;
          } else {
            const firstOIndex = results.indexOf('O');
            if (firstOIndex !== -1 && res === 'X') {
              totalFailed++;
            }
          }
        }
      });
    };

    for (let i = 0; i <= heightIndex; i++) {
      const h = heights[i];
      processHeight(h, lastCleared);
    }
    
    return totalFailed;
  }, [hasValidParticipantData, participantsResults, getLastHeightCleared, heights, safeArray]);

  // Función para calcular la mejor marca y detalles para ranking - VERSIÓN CORREGIDA
  const calculateBestMarkDetails = useCallback((participantId) => {
    if (!hasValidParticipantData(participantId)) {
      console.warn(`Participant results not found for id: ${participantId}`);
      return { bestMark: '-', attemptsAtBestMark: 0, totalFailedAttemptsUpToBestMark: 0 };
    }
    
    const participant = participantsResults[participantId];
    const specialStatus = participant.specialStatus;
    
    // Si el estado especial es diferente a 'Valid', lo ignoramos para el ranking
    if (specialStatus && specialStatus !== 'Valid') {
      return { bestMark: specialStatus, attemptsAtBestMark: 0, totalFailedAttemptsUpToBestMark: 0 };
    }
    
    const lastCleared = getLastHeightCleared(participantId);
    const heightKey = lastCleared ? lastCleared.toFixed(2) : null;
    let attemptsAtBestMark = 0;
    let totalFailedAttemptsUpToBestMark = 0;

    if (lastCleared && participant.heights && participant.heights[heightKey]) {
      const heightResult = participant.heights[heightKey];
      const attempts = safeArray(heightResult.attempts);
      const isTiebreak = (heights || []).find(h => h.value === lastCleared)?.isTiebreak;
      
      if (isTiebreak) {
        attemptsAtBestMark = attempts[0] !== '' ? 1 : 0;
      } else {
        const firstOIndex = attempts.indexOf('O');
        attemptsAtBestMark = firstOIndex !== -1 ? firstOIndex + 1 : 3;
      }

      const heightIndex = (heights || []).findIndex(h => h.value === lastCleared);
      
      const processHeight = (h) => {
        const hKey = h.value.toFixed(2);
        const hResult = participant.heights[hKey];
        if (!hResult || !hResult.attempts) return;
        
        const hAttempts = safeArray(hResult.attempts);
        hAttempts.forEach(a => {
          if (a === 'X') totalFailedAttemptsUpToBestMark++;
        });
      };

      for (let i = 0; i <= heightIndex; i++) {
        const h = heights[i];
        processHeight(h);
      }
    }

    const bestMark = lastCleared ? `${lastCleared.toFixed(2)} m` : 'NM';
    return { bestMark, attemptsAtBestMark, totalFailedAttemptsUpToBestMark };
  }, [hasValidParticipantData, participantsResults, getLastHeightCleared, heights, safeArray]);

  // Función para cancelar el desempate
  const handleCancelTiebreak = () => {
    setOpenTiebreakModal(false);
    setTiebreakParticipants([]);
    setTiebreakHeight(null);
    setSnackbar({
      open: true,
      message: 'Desempate cancelado.',
      severity: 'info',
    });
  };

  // Función para manejar las opciones de desempate
  const handleDesempateOption = (option, newHeightValue) => {
    if (option === 'Agregar') {
      if (!newHeightValue || newHeightValue <= 0) {
        setSnackbar({
          open: true,
          message: 'Por favor, ingresa una altura válida mayor que cero.',
          severity: 'error',
        });
        return;
      }
      // Verificar si la altura ya existe (no para alturas de desempate)
      if ((heights || []).some(h => h.value === parseFloat(newHeightValue) && !h.isTiebreak)) {
        setSnackbar({
          open: true,
          message: 'Esta altura ya existe.',
          severity: 'error',
        });
        return;
      }
      // Añadir la nueva altura como altura de desempate
      const parsedHeight = parseFloat(newHeightValue).toFixed(2);
      const newArr = [...heights,
         { value: parseFloat(parsedHeight), isTiebreak:true }]
         .sort((a,b)=>a.value-b.value);
        updateHeatHeights(heatKey, newArr);
      setTiebreakHeight(parseFloat(parsedHeight));
            // Actualizar participantsResults para incluir la nueva altura
            setParticipantsResults(prev => {
              const updatedResults = { ...prev };
              for (const participant of participantsArray) {
                if (!updatedResults[participant.idNumber]) {
                  updatedResults[participant.idNumber] = {
                    specialStatus: null,
                    heights: {},
                    notes: '',
                    points: 0
                  };
                }
                   else if (!updatedResults[participant.idNumber].heights) {
                     updatedResults[participant.idNumber].heights = {};
                     }
                updatedResults[participant.idNumber].heights[parsedHeight] = {
                  attempts: ['']
                };
              }
              return updatedResults;
            });
            
            // Persistir a Firestore
            setTimeout(() => {
              participantsArray.forEach(participant => {
                updateParticipantResults(
                  heatDetails.eventNumber,
                  heatDetails.round,
                  heatDetails.heat,
                  participant.idNumber,
                  participantsResults[participant.idNumber] || {
                    specialStatus: null,
                    heights: {},
                    notes: '',
                    points: 0
                  }
                );
              });
            }, 0);
            
      setSnackbar({
        open: true,
        message: `Altura de desempate ${parsedHeight} m añadida correctamente.`,
        severity: 'success',
      });
    } else if (option === 'Mantener Empate') {
      setSnackbar({
        open: true,
        message: 'Empate mantenido en el primer lugar.',
        severity: 'info',
      });
    }

    setOpenTiebreakModal(false);
  };

  // Función para abrir el modal de marca de siembra
  const handleOpenSeedMarkModal = (participant) => {
    setSelectedSeedParticipant(participant);
    setOpenSeedMarkModal(true);
  };

  // Función para guardar marca de siembra
  const handleSaveSeed = async (idNumber, seedMark) => {
    try {
      await saveSeedMarks(idNumber, eventCode, seedMark);
      
      // Actualizar el estado local de marcas de siembra
      setSeedMarks(prevMarks => ({
        ...prevMarks,
        [idNumber]: seedMark
      }));
      
      setOpenSeedMarkModal(false);
      setSnackbar({ 
        open: true, 
        message: `Marca de siembra guardada: ${seedMark}`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error al guardar marca de siembra:', error);
      setSnackbar({ 
        open: true, 
        message: `Error al guardar marca: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  // Función para eliminar marca de siembra
  const handleDeleteSeedMark = (participantId) => {
    try {
      saveSeedMarks(participantId, eventCode, "");
      
      // Actualizar el estado local
      setSeedMarks(prevMarks => {
        const newMarks = { ...prevMarks };
        delete newMarks[participantId]; // Eliminar la entrada del mapa
        return newMarks;
      });
      
      setOpenSeedMarkModal(false);
      setSelectedSeedParticipant(null);
      
      setSnackbar({ 
        open: true, 
        message: "Marca de siembra eliminada", 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error al eliminar marca de siembra:', error);
      setSnackbar({ 
        open: true, 
        message: `Error al eliminar marca: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  // Handle notes update
  const handleNotesUpdate = (participantId, notes) => {
    updateParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      participantId,
      { notes }
    );
    setSnackbar({ open: true, message: 'Notas guardadas.', severity: 'success' });
  };
  
  // Función para abrir el modal de resultados
  const handleOpenResultModal = (participant, height, trialIndex) => {
    const specialStatus = participantsResults[participant.idNumber]?.specialStatus;
    if (specialStatus && specialStatus !== 'Valid') {
      // No permitir editar resultados si hay un status especial, excepto si es 'Valid'
      return;
    }
    setSelectedParticipant(participant);
    setSelectedHeight(height);
    setCurrentTrialIndex(trialIndex);
    setOpenResultModal(true);
  };

  // Función para cerrar el modal de resultados
  const handleCloseResultModal = () => {
    setOpenResultModal(false);
    setSelectedHeight(null);
    setSelectedParticipant(null);
    setCurrentTrialIndex(null);
  };

  // Función para manejar el ingreso de un resultado en un intento
  const handleTrialResult = (result) => {
    if (
      !selectedParticipant ||
      selectedHeight === null ||
      currentTrialIndex === null
    )
      return;
  
    const participantId = selectedParticipant.idNumber;
    const heightKey     = selectedHeight.value.toFixed(2);
    const trialIndex    = currentTrialIndex;
  
    /* ---------------------------------------------------------
     * 1. Actualizamos el estado local (para refrescar la tabla)
     * ------------------------------------------------------- */
    setParticipantsResults(prev => {
      const updated = { ...prev };
  
      // 1-a)  Aseguramos la estructura mínima
      if (!updated[participantId]) {
        updated[participantId] = {
          specialStatus: null,
          heights: {},
          notes: '',
          points: 0,
        };
      }
      if (!updated[participantId].heights[heightKey]) {
        updated[participantId].heights[heightKey] = {
          attempts: selectedHeight.isTiebreak ? [''] : ['', '', ''],
        };
      }
  
      // 1-b)  Guardamos el resultado en el intento correspondiente
      const attemptsArr = safeArray(
        updated[participantId].heights[heightKey].attempts
      );
      attemptsArr[trialIndex] =
        result; /* ‘O’, ‘X’, ‘-’, ‘R’                     */
      updated[participantId].heights[heightKey].attempts = attemptsArr;
  
      /* ---------------------------------------------
       * 1-c)  Lógica de reglas de verticales (foul, R)
       * ------------------------------------------- */
      if (!selectedHeight.isTiebreak) {
        const xCount = attemptsArr.filter(a => a === 'X').length;
  
        /* 3 fallos consecutivos  → FOUL (eliminado) */
        if (xCount >= 3) {
          updated[participantId].specialStatus = 'Foul';
  
          const start = (heights || []).findIndex(
            h => h.value === selectedHeight.value
          );
          for (let h = start + 1; h < (heights || []).length; h++) {
            const hKey = heights[h].value.toFixed(2);
            updated[participantId].heights[hKey] = {
              attempts: heights[h].isTiebreak ? [''] : ['', '', ''],
            };
          }
  
          setSnackbar({
            open: true,
            message: `Atleta eliminado por tres fallos en ${selectedHeight.value.toFixed(
              2
            )} m.`,
            severity: 'warning',
          });
        }
  
        /* Salto válido (‘O’) */
        else if (result === 'O') {
          updated[participantId].specialStatus = 'Valid';
          setSnackbar({
            open: true,
            message: `Altura franqueada en ${selectedHeight.value.toFixed(
              2
            )} m.`,
            severity: 'success',
          });
        }
  
        /* Retirada (‘R’) */
        else if (result === 'R') {
          const curIdx = (heights || []).findIndex(
            h => h.value === selectedHeight.value
          );
          const hasPrevSuccess = (heights || [])
            .slice(0, curIdx + 1)
            .some(h => {
              const key   = h.value.toFixed(2);
              const atts  =
                updated[participantId].heights[key]?.attempts || [];
              return atts.includes('O');
            });
  
          updated[participantId].specialStatus = hasPrevSuccess
            ? 'Valid'
            : 'NM';
  
          for (let h = curIdx + 1; h < (heights || []).length; h++) {
            const hKey = heights[h].value.toFixed(2);
            updated[participantId].heights[hKey] = {
              attempts: heights[h].isTiebreak ? [''] : ['', '', ''],
            };
          }
  
          setSnackbar({
            open: true,
            message: hasPrevSuccess
              ? 'Atleta retirado. Se conserva la mejor marca válida.'
              : 'Atleta retirado sin marca válida.',
            severity: hasPrevSuccess ? 'info' : 'warning',
          });
        }
      }
  
      return updated;
    });
  
    /* -----------------------------------------------------------------
     * 2. Persistimos inmediatamente en Firestore  (merge por participante)
     * ---------------------------------------------------------------- */
    updateParticipantResults(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      participantId,
      participantsResults[participantId] /* el objeto ya quedó actualizado */
    )
      /* -----------------------------------------------------------------
       * 3. En cuanto Firestore responde, pedimos al contexto recalcular
       *    los lugares; eso actualiza `place` / `passed` para todos los
       *    atletas de este heat y los deja en Firestore también.
       * ---------------------------------------------------------------- */
      .then(() => {
        calculateRankings(
          heatDetails.eventNumber,
          heatDetails.round,
          heatDetails.heat
        );
      })
      .catch(err => console.error('🔥 error guardando intento:', err));
  
    /* --------------------
     * 4. Cerrar el modal
     * ------------------ */
    handleCloseResultModal();
  };

  // Función para abrir el modal de alturas
  const handleOpenHeightModal = () => {
    if (!heights) return;
    setIsEditingHeight(false);
    const nextHeightValue = (heights.length > 0 ? heights[heights.length - 1].value + (increment || 0.05) : initialHeight);
    setCurrentHeight(parseFloat(nextHeightValue.toFixed(2)));
    setOpenHeightModal(true);
  };

  // Función para abrir el modal de edición de altura
  const handleOpenEditHeightModal = (height) => {
    setIsEditingHeight(true);
    setCurrentHeight(height.value);
    setSelectedHeight(height);
    setOpenHeightModal(true);
  };

  // Función para cerrar el modal de alturas
  const handleCloseHeightModal = () => {
    setOpenHeightModal(false);
    setSelectedHeight(null);
    setIsEditingHeight(false);
  };

  // Función añadir o editar una altura
  const addOrEditHeight = async () => {
    if (currentHeight <= 0) {
      setSnackbar({
        open: true,
        message: 'Por favor, ingresa una altura válida mayor que cero.',
        severity: 'error'
      });
      return;
    }
  
    const currentHeightKey = currentHeight.toFixed(2);
    const selectedHeightKey = selectedHeight ? selectedHeight.value.toFixed(2) : null;
  
    // Bloquear listeners por más tiempo
    setModificationFlag(true, 3000); // 3 segundos en lugar de 1
  
    try {
      if (isEditingHeight) {
        if ((heights || []).some(h => h.value === currentHeight && h.value !== selectedHeight?.value)) {
          setSnackbar({
            open: true,
            message: 'Esta altura ya existe.',
            severity: 'error'
          });
          return;
        }
  
        // Actualización atómica: Hacer todos los cambios antes de persistir
        const newHeightsArray = (heights || [])
          .map(h => h.value === selectedHeight.value ? { ...h, value: currentHeight } : h)
          .sort((a, b) => a.isTiebreak === b.isTiebreak ? a.value - b.value : a.isTiebreak ? 1 : -1);
  
        // Preparar todos los datos de participantes
        const participantUpdates = [];
        for (const participant of participantsArray) {
          const pid = participant.idNumber;
          const currentData = participantsResults[pid] || {
            specialStatus: null,
            heights: {},
            notes: '',
            points: 0
          };
  
          let updatedData = JSON.parse(JSON.stringify(currentData)); // Deep copy
  
          if (selectedHeightKey && currentHeightKey !== selectedHeightKey) {
            if (updatedData.heights?.[selectedHeightKey]) {
              updatedData.heights[currentHeightKey] = { ...updatedData.heights[selectedHeightKey] };
              delete updatedData.heights[selectedHeightKey];
            }
          }
  
          participantUpdates.push({
            pid,
            data: updatedData,
            keysToDelete: (currentHeightKey !== selectedHeightKey && selectedHeightKey) ? [selectedHeightKey] : []
          });
        }
  
        // Ejecutar todas las operaciones de una vez
        // Primero actualizar alturas
        await updateHeatHeights(heatKey, newHeightsArray);
        
        // Luego actualizar todos los participantes en paralelo
        await Promise.all(
          participantUpdates.map(({ pid, data, keysToDelete }) =>
            updateParticipantResults(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              pid,
              data,
              keysToDelete
            )
          )
        );
  
        // Actualizar estado local al final
        setParticipantsResults(prev => {
          const updated = { ...prev };
          participantUpdates.forEach(({ pid, data }) => {
            updated[pid] = data;
          });
          return updated;
        });
  
        setSnackbar({
          open: true,
          message: 'Altura editada correctamente.',
          severity: 'success'
        });
  
      } else {
        // Crear nueva altura - similar enfoque atómico
        if ((heights || []).some(h => h.value === currentHeight)) {
          setSnackbar({
            open: true,
            message: 'Esta altura ya existe.',
            severity: 'error'
          });
          return;
        }
  
        const newHeightsArray = [...heights, { value: currentHeight, isTiebreak: false }]
          .sort((a, b) => a.value - b.value);
  
        const participantUpdates = [];
        for (const participant of participantsArray) {
          const pid = participant.idNumber;
          const currentData = participantsResults[pid] || {
            specialStatus: null,
            heights: {},
            notes: '',
            points: 0
          };
  
          const updatedData = JSON.parse(JSON.stringify(currentData));
          if (!updatedData.heights) updatedData.heights = {};
          updatedData.heights[currentHeightKey] = { attempts: ['', '', ''] };
  
          participantUpdates.push({ pid, data: updatedData });
        }
  
        // Operaciones atómicas
        await updateHeatHeights(heatKey, newHeightsArray);
        
        await Promise.all(
          participantUpdates.map(({ pid, data }) =>
            updateParticipantResults(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              pid,
              data
            )
          )
        );
  
        setParticipantsResults(prev => {
          const updated = { ...prev };
          participantUpdates.forEach(({ pid, data }) => {
            updated[pid] = data;
          });
          return updated;
        });
  
        setSnackbar({
          open: true,
          message: 'Altura añadida correctamente.',
          severity: 'success'
        });
      }
  
    } catch (error) {
      console.error('Error modificando altura:', error);
      setSnackbar({
        open: true,
        message: 'Error al modificar la altura.',
        severity: 'error'
      });
    } finally {
      handleCloseHeightModal();
    }
  };

  // Función eliminar una altura
  const handleDeleteHeight = async () => {
    if (!selectedHeight) return;
    
    const heightToDelete = selectedHeight;
    const heightKey = heightToDelete.value.toFixed(2);
  
    // Bloquear listeners
    setModificationFlag(true, 3000);
  
    try {
      const newHeightsArray = heights.filter(h => h.value !== heightToDelete.value);
      
      // Preparar actualizaciones de participantes
      const participantUpdates = [];
      for (const participant of participantsArray) {
        const pid = participant.idNumber;
        const currentData = participantsResults[pid];
        
        if (currentData?.heights?.[heightKey]) {
          const updatedData = JSON.parse(JSON.stringify(currentData));
          delete updatedData.heights[heightKey];
          participantUpdates.push({ pid, data: updatedData });
        }
      }
  
      // Operaciones atómicas
      await updateHeatHeights(heatKey, newHeightsArray);
      
      await Promise.all(
        participantUpdates.map(({ pid, data }) =>
          updateParticipantResults(
            heatDetails.eventNumber,
            heatDetails.round,
            heatDetails.heat,
            pid,
            data,
            [heightKey] // Eliminar clave en Firestore
          )
        )
      );
  
      // Actualizar estado local
      setParticipantsResults(prev => {
        const updated = { ...prev };
        participantUpdates.forEach(({ pid, data }) => {
          updated[pid] = data;
        });
        return updated;
      });
  
      setSnackbar({
        open: true,
        message: `Altura ${heightToDelete.value.toFixed(2)} m eliminada correctamente.`,
        severity: 'info',
      });
  
    } catch (error) {
      console.error('Error eliminando altura:', error);
      setSnackbar({
        open: true,
        message: 'Error al eliminar la altura.',
        severity: 'error'
      });
    } finally {
      handleCloseHeightModal();
    }
  };
  
  // Cleanup en useEffect para limpiar timeouts
  useEffect(() => {
    return () => {
      if (modificationTimeoutRef.current) {
        clearTimeout(modificationTimeoutRef.current);
      }
    };
  }, []);

  // Función para eliminar un resultado
  const handleDeleteResult = () => {
    if (!selectedParticipant || selectedHeight === null || currentTrialIndex === null) return;

    const participantId = selectedParticipant.idNumber;
    const heightKey = selectedHeight.value.toFixed(2);

    setParticipantsResults((prev) => {
      const updatedResults = { ...prev };
      if (updatedResults[participantId] && updatedResults[participantId].heights[heightKey]) {
        const attempts = safeArray(updatedResults[participantId].heights[heightKey].attempts);
        attempts[currentTrialIndex] = '';
        updatedResults[participantId].heights[heightKey].attempts = attempts;

        // Revisar estado especial después de eliminar el resultado
        if (!selectedHeight.isTiebreak) {
          const xCount = attempts.filter((a) => a === 'X').length;
          if (xCount < 3 && updatedResults[participantId].specialStatus === 'Foul') {
            updatedResults[participantId].specialStatus = null;
          }

          if (attempts.includes('R')) {
            // Recalcular estado
            const currentHeightIndex = (heights || []).findIndex((h) => h.value === selectedHeight.value);
            let hasPreviousSuccess = false;
            for (let h = 0; h <= currentHeightIndex; h++) {
              const hKey = heights[h].value.toFixed(2);
              const hAttempts = safeArray(updatedResults[participantId].heights[hKey]?.attempts);
              if (hAttempts.includes('O')) {
                hasPreviousSuccess = true;
                break;
              }
            }
            updatedResults[participantId].specialStatus = hasPreviousSuccess ? 'Valid' : 'NM';
          }
        }
      }
      
      // Persistir INMEDIATAMENTE con los datos actualizados
      updateParticipantResults(
        heatDetails.eventNumber,
        heatDetails.round,
        heatDetails.heat,
        participantId,
        updatedResults[participantId]
      );
      
      return updatedResults;
    });

    setSnackbar({
      open: true,
      message: 'Resultado eliminado correctamente.',
      severity: 'info',
    });

    handleCloseResultModal();
  };

  // Handler para añadir un carril
  const handleAddLane = () => {
    setLanes(prev => [...prev, null]);
    setLanesCount(prev => prev + 1);
    setSnackbar({ open: true, message: `Carril añadido.`, severity: 'success' });
  };

  // Handler para agregar/reemplazar inline
  const handleInlineAdd = async (laneNumber, newIdNumber) => {
    const trimmed = newIdNumber.trim();
    // ¿Ya existe en este heat?
    if (participantsArray.some(p => p.idNumber === trimmed)) {
      setSnackbar({
        open: true,
        message: `El atleta ${trimmed} ya está en este heat.`,
        severity: 'error'
      });
      return;
    }
    // ¿Existe en la base de atletas?
    const found = pplData.find(p => p.idNumber === trimmed);
    if (!found) {
      setSnackbar({
        open: true,
        message: `Atleta ${trimmed} no encontrado.`,
        severity: 'error'
      });
      return;
    }
    // Añadimos normalmente
    updateHeatParticipants(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      found.idNumber,
      laneNumber,
      'add',
      { ...found, lane: laneNumber }
    );
    setSnackbar({
      open: true,
      message: `${found.fullName} añadido al carril ${laneNumber}.`,
      severity: 'success'
    });
  };

  // Añadir un atleta en un carril vacío (vía prompt)
  const handleAddAthlete = (laneNumber) => {
    const idNum = window.prompt('Ingrese el número de atleta (idNumber):');
    if (!idNum) return;
    const found = pplData.find(p => p.idNumber === idNum.trim());
    if (!found) {
      setSnackbar({ open: true, message: `Atleta con idNumber ${idNum} no encontrado.`, severity: 'error' });
      return;
    }
    if (participantsArray.some(p => p.idNumber === found.idNumber)) {
      setSnackbar({ open: true, message: `${found.fullName} ya está en este heat.`, severity: 'warning' });
      return;
    }
    updateHeatParticipants(
      heatDetails.eventNumber,
      heatDetails.round,
      heatDetails.heat,
      found.idNumber,
      laneNumber,
      'add',
      { ...found, lane: laneNumber }
    );
    setSnackbar({ open: true, message: `${found.fullName} añadido al carril ${laneNumber}.`, severity: 'success' });
  };

  // Función para manejar acción especial
  const handleSpecialAction = (participantId, specialStatus) => {
    setParticipantsResults((prev) => {
      const updatedResults = { ...prev };
      if (updatedResults[participantId]) {
        updatedResults[participantId].specialStatus = specialStatus;
        // Si no es "Valid", limpiar todos los intentos
        if (specialStatus && specialStatus !== 'Valid') {
          const pHeights = updatedResults[participantId].heights || {};
          for (let hKey in pHeights) {
            const isTiebreakH = (heights || []).find(h => h.value.toFixed(2) === hKey)?.isTiebreak;
            pHeights[hKey].attempts = isTiebreakH ? [''] : ['', '', ''];
          }
        }
      }
      return updatedResults;
    });

    // Actualizar en el contexto
    setTimeout(() => {
      updateParticipantResults(
        heatDetails.eventNumber,
        heatDetails.round,
        heatDetails.heat,
        participantId,
        participantsResults[participantId] || {
          specialStatus: null,
          heights: {},
          notes: '',
          points: 0
        }
      );
    }, 0);

    setSnackbar({
      open: true,
      message: `Estado especial: ${specialStatus || 'Válido'}`,
      severity: 'info',
    });
  };

  // Función para determinar si un intento debe estar deshabilitado
  const isAttemptDisabled = (participantId, height, trialIndex) => {
    const participant = participantsResults[participantId];
    if (!participant) return true;

    if (participant.specialStatus === 'Foul') return true;

    for (let h of heights || []) {
      if (h.value > height.value) break;
      const hKey = h.value.toFixed(2);
      const attempts = safeArray(participant.heights?.[hKey]?.attempts);
      for (let i = 0; i < attempts.length; i++) {
        const attempt = attempts[i];
        if (attempt === 'R') {
          if (h.value < height.value) return true;
          if (h.value === height.value && i < trialIndex) return true;
        }
      }
    }

    const currentHeightKey = height.value.toFixed(2);
    const heightAttempts = safeArray(participant.heights?.[currentHeightKey]?.attempts);
    for (let i = 0; i < trialIndex; i++) {
      const attempt = heightAttempts[i];
      if (attempt === 'O' || attempt === '-') {
        return true;
      }
    }

    if (height.isTiebreak && !(tiebreakParticipants || []).includes(participantId)) {
      return true;
    }

    return false;
  };

  // Para DnD
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = lanes.findIndex((lane, idx) =>
      lane ? String(lane.idNumber) === active.id : `empty-${idx + 1}` === active.id
    );
    const newIndex = lanes.findIndex((lane, idx) =>
      lane ? String(lane.idNumber) === over.id : `empty-${idx + 1}` === over.id
    );

    if (oldIndex < 0 || newIndex < 0) return;
    
    const newLanes = [...lanes];
    const moved = newLanes[oldIndex];
    newLanes[oldIndex] = newLanes[newIndex];
    newLanes[newIndex] = moved;

    newLanes.forEach((ath, idx) => {
      if (ath) {
        updateHeatParticipants(
          heatDetails.eventNumber,
          heatDetails.round,
          heatDetails.heat,
          ath.idNumber,
          idx + 1,
          'update'
        );
      }
    });

    setLanes(newLanes);
    setSnackbar({
      open: true,
      message: 'Se intercambiaron los atletas correctamente.',
      severity: 'success',
    });
  };

  // Items para dnd-kit
  const items = lanes.map((lane, idx) => 
    lane ? String(lane.idNumber) : `empty-${idx + 1}`
  );

  // Renderizar celdas de altura - VERSIÓN CORREGIDA
  const renderHeightCells = (athlete) => {
    if (!athlete) {
      return heights.map((height) => (
        <TableCell key={height.value} align="center" sx={responsiveStyles.tableCell}>-</TableCell>
      ));
    }

    const participantId = athlete.idNumber;
    const specialStatus = participantsResults[participantId]?.specialStatus || null;
    const isSpecial = specialStatus && specialStatus !== 'Valid';

    return heights.map((height) => {
      const heightKey = height.value.toFixed(2);
      
      // Usar la función helper para obtener resultados de forma segura
      const heightResult = getHeightResult(participantId, heightKey, height.isTiebreak);
      const attempts = safeArray(heightResult.attempts);
      
      const isTiebreakAttempt = height.isTiebreak && !(tiebreakParticipants || []).includes(participantId);

      return (
        <TableCell key={`${participantId}-${height.value}`} align="center" sx={responsiveStyles.tableCell}>
          <Stack direction="row" spacing={1} justifyContent="center">
            {height.isTiebreak ? (
              <Button
                variant="outlined"
                onClick={() => handleOpenResultModal(athlete, height, 0)} // Solo un intento
                disabled={
                  isSpecial ||
                  isTiebreakAttempt ||
                  isAttemptDisabled(participantId, height, 0)
                }
                sx={{
                  ...responsiveStyles.button,
                  minWidth: isMobile ? '30px' : '40px',
                  padding: isMobile ? '2px' : '4px',
                }}
              >
                {attempts[0] === '' ? '+' : attempts[0]}
              </Button>
            ) : (
              attempts.map((attempt, idx) => (
                <Button
                  key={idx}
                  variant="outlined"
                  onClick={() => handleOpenResultModal(athlete, height, idx)}
                  disabled={
                    isSpecial ||
                    isTiebreakAttempt ||
                    isAttemptDisabled(participantId, height, idx)
                  }
                  sx={{
                    ...responsiveStyles.button,
                    minWidth: isMobile ? '30px' : '40px',
                    padding: isMobile ? '2px' : '4px',
                  }}
                >
                  {attempt === '' ? '+' : attempt}
                </Button>
              ))
            )}
          </Stack>
        </TableCell>
      );
    });
  };

  // Renderizar celda marca de siembra
  const renderSeedMarkCell = (athlete) => {
    if (!athlete) {
      return <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>;
    }

    return (
      <TableCell align="center" sx={responsiveStyles.tableCell}>
        <Button 
          variant="outlined" 
          size="small"
          onClick={() => handleOpenSeedMarkModal(athlete)}
          sx={{
            ...responsiveStyles.button,
            color: 'success.main',
            borderColor: 'success.main',
            '&:hover': {
              borderColor: 'success.dark',
              backgroundColor: 'success.light',
              opacity: 0.9,
            },
            ...(seedMarks[athlete.idNumber] ? {
              fontWeight: 'bold',
              backgroundColor: 'rgba(76, 175, 80, 0.1)'
            } : {})
          }}
        >
          {seedMarks[athlete.idNumber] || '—'}
        </Button>
      </TableCell>
    );
  };

  // Renderizar mejor marca y lugar
  const renderBestMarkAndPlace = (athlete) => {
    if (!athlete) {
      return (
        <>
          <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>
          <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>
        </>
      );
    }
  
    const participantId      = athlete.idNumber;
    const pr                 = participantsResults[participantId] || {};
    const specialStatus      = pr.specialStatus;         // DNS, DNF, Foul, etc.
    const lastCleared        = getLastHeightCleared(participantId);   // Altura num.
    const hasValidClearance  = typeof lastCleared === 'number';
  
    /* -------------------------------------------------------------------------
     *  Regla nueva ▸ Foul se muestra como marca VÁLIDA si hubo al menos un ‘O’.
     *               Cuando NO hay ‘O’ ⇒  NM  (y el lugar se deja en “–”)
     * ----------------------------------------------------------------------- */
    let bestMarkDisplay;
    let blockPlaceAndGlobal;        // ¿ocultar lugar general y del heat?
    
    if (hasValidClearance) {
      bestMarkDisplay    = `${lastCleared.toFixed(2)} m`;
      blockPlaceAndGlobal = false;            // Se calcula lugar normalmente
    } else {
      // Sin altura franqueada
      bestMarkDisplay    = 'NM';
      blockPlaceAndGlobal = true;             // Lugar “–”
    }
  
    return (
      <>
        {/* Mejor marca ------------------------------------------------------- */}
        <TableCell align="center" sx={responsiveStyles.tableCell}>
          <span style={{
            fontWeight: hasValidClearance ? 'bold' : 'normal',
            color     : hasValidClearance ? 'green' : 'inherit'
          }}>
            {bestMarkDisplay}
          </span>
        </TableCell>
  
        {/* Lugar dentro del heat -------------------------------------------- */}
        <TableCell align="center" sx={responsiveStyles.tableCell}>
          { blockPlaceAndGlobal
              ? '–'
              : getPlace(
                  heatDetails.eventNumber,
                  heatDetails.round,
                  heatDetails.heat,
                  participantId
                )
          }
        </TableCell>
      </>
    );
  };

  // Renderizar lugar general
  const renderGeneralPlaceCell = (athlete) => {
    if (!athlete) {
      return <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>;
    }

    const globalPlace = globalPlaces[athlete.idNumber] ?? '-';
    return (
      <TableCell 
        align="center" 
        sx={{ 
          ...responsiveStyles.tableCell,
          fontWeight: globalPlace !== '-' ? 'bold' : 'normal',
          color: globalPlace !== '-' ? 'text.primary' : 'text.secondary' 
        }}
      >
        {globalPlace}
      </TableCell>
    );
  };

  // Renderizar columna de puntos
  const renderPointsCell = (athlete) => {
    if (!athlete || !enablePoints) {
      return null;
    }

    return (
      <TableCell align="center" sx={responsiveStyles.tableCell}>
        {participantsResults[athlete.idNumber]?.points || 0}
      </TableCell>
    );
  };

  // Renderizar columna de notas
  const renderNotesCell = (athlete) => {
    if (!athlete) {
      return <TableCell align="center" sx={responsiveStyles.tableCell}>-</TableCell>;
    }

    const notes = participantsResults[athlete.idNumber]?.notes || '';

    return (
      <TableCell align="center" sx={responsiveStyles.tableCell}>
        <TextField
          size="small"
          value={notes}
          onChange={(e) => handleNotesUpdate(athlete.idNumber, e.target.value)}
          variant="outlined"
          placeholder="Notas"
          sx={responsiveStyles.inputField}
        />
      </TableCell>
    );
  };

  // Renderizar columna de acciones
  const renderActionsCell = athlete => (
    <TableCell align="center" sx={responsiveStyles.tableCell}>
      { athlete ? (
        <ActionColumn
          participant={athlete}
          handleEditLane={(upd, newLane) => {
            updateHeatParticipants(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              upd.idNumber, 
              Number(newLane), 
              'update'
            );
            setSnackbar({ open:true, message:'Carril actualizado.', severity:'success' });
          }}
          handleRemoveAthlete={rem => {
            updateHeatParticipants(
              heatDetails.eventNumber,
              heatDetails.round,
              heatDetails.heat,
              rem.idNumber, 
              null, 
              'remove'
            );
            setSnackbar({ open:true, message:'Atleta eliminado.', severity:'info' });
          }}
          handleActionSelect={spec => handleSpecialAction(athlete.idNumber, spec)}
          isTrackEvent={false}
          responsiveStyles={responsiveStyles}
        />
      ) : '—' }
    </TableCell>
  );

  return (
    <Box sx={{ padding: isMobile ? 1 : 2 }}>
      {/* Botones Principales: Añadir Altura, Calcular lugar */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {/* Botón para añadir altura */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenHeightModal}
          sx={{ ...responsiveStyles.button }}
          startIcon={<AddIcon />}
        >
          {isMobile ? "Altura" : "Añadir Altura"}
        </Button>

        {/* Botón para calcular lugares */}
        <Button
          variant="contained"
          color="secondary"
          onClick={calculateRankings}
          sx={{ ...responsiveStyles.button }}
          startIcon={<CalculateIcon />}
        >
          {isMobile ? "Calcular" : "Calcular lugar"}
        </Button>
      </Box>

      {/* Tabla de participantes con DnD */}
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <TableContainer 
            component={Paper}
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[300],
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.grey[100],
              },
            }}
          >
            <Table stickyHeader size={isMobile ? "small" : isTablet ? "medium" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Mv" : "Mover"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "C" : "Carril"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Num" : "Número"}</TableCell>
                  <TableCell align="left" sx={responsiveStyles.tableCell}>{isMobile ? "Nombre" : "Nombre y Equipo"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Siemb" : "Siembra"}</TableCell>
                  
                  {/* Columnas de alturas */}
                  {heights.map((height) => (
                    <TableCell
                      key={height.value}
                      align="center"
                      sx={{ 
                        ...responsiveStyles.tableCell,
                        cursor: 'pointer' 
                      }}
                      onClick={() => handleOpenEditHeightModal(height)}
                    >
                      {isMobile ? 
                        `${height.value.toFixed(2)}${height.isTiebreak ? '*' : ''}` : 
                        `${height.value.toFixed(2)} m ${height.isTiebreak ? '(D)' : ''}`}
                    </TableCell>
                  ))}
                  
                  {/* Mejor marca y lugar */}
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Mejor" : "Mejor Marca"}</TableCell>
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "L" : "Lugar"}</TableCell>
                  
                  {/* Lugar general */}
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "L.Gen" : "Lugar Gen"}</TableCell>
                  
                  {/* Puntos (opcional) */}
                  {enablePoints && 
                    <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Pts" : "Puntos"}</TableCell>
                  }
                  
                  {/* Notas */}
                  <TableCell align="center" sx={responsiveStyles.tableCell}>{isMobile ? "Not" : "Notas"}</TableCell>
                  
                  {/* Acciones */}
                  <TableCell align="center" sx={responsiveStyles.tableCell}>
                    <ActionColumnHeader
                      handleAddAthlete={handleAddLane}
                      responsiveStyles={responsiveStyles}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lanes.map((athlete, index) => {
                  const laneNumber = index + 1;
                  const isPassed = athlete && hasPassed(
                    heatDetails.eventNumber,
                    heatDetails.round,
                    heatDetails.heat,
                    athlete.idNumber
                  );
                  const rowStyle = isPassed
                    ? { backgroundColor: '#c8e6c9' }
                    : index % 2 === 0
                      ? { backgroundColor: theme.palette.action.hover }
                      : {};

                  return (
                    <LaneSortableItem
                      key={athlete ? `ath-${athlete.idNumber}` : `empty-${laneNumber}`}
                      laneNumber={laneNumber}
                      athlete={athlete}
                      onAddAthlete={handleAddAthlete}
                      onReplaceAthlete={handleInlineAdd}
                      additionalCells={
                        <>
                          {/* Marca de Siembra */}
                          {renderSeedMarkCell(athlete)}
                          
                          {/* Alturas */}
                          {renderHeightCells(athlete)}
                          
                          {/* Mejor marca y lugar */}
                          {renderBestMarkAndPlace(athlete)}
                          
                          {/* Lugar General */}
                          {renderGeneralPlaceCell(athlete)}
                          
                          {/* Puntaje (opcional) */}
                          {enablePoints && renderPointsCell(athlete)}
                          
                          {/* Notas */}
                          {renderNotesCell(athlete)}
                          
                          {/* Acciones */}
                          {renderActionsCell(athlete)}
                        </>
                      }
                      rowStyle={rowStyle}
                      cellStyle={responsiveStyles.tableCell}
                      buttonStyle={responsiveStyles.button}
                      typographyStyle={responsiveStyles.typography}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>

      {/* Modal para añadir o editar altura */}
      <Modal open={openHeightModal} onClose={handleCloseHeightModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: isMobile ? 2 : 4,
            width: isMobile ? '85%' : '300px',
            borderRadius: 2,
          }}
        >
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={handleCloseHeightModal}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            {isEditingHeight ? 'Editar Altura' : 'Añadir Altura'}
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Altura (m)"
            value={currentHeight}
            onChange={(e) => setCurrentHeight(parseFloat(e.target.value) || 0)}
            sx={{ mb: 2 }}
            inputProps={{ step: 0.01, min: 0 }}
          />
          <Button variant="contained" color="primary" onClick={addOrEditHeight}>
            {isEditingHeight ? 'Guardar Cambios' : 'Agregar'}
          </Button>
          {isEditingHeight && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteHeight}
              sx={{ mt: 2 }}
            >
              Eliminar Altura
            </Button>
          )}
        </Box>
      </Modal>

      {/* Modal para añadir o eliminar resultados */}
      <Modal open={openResultModal} onClose={handleCloseResultModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: isMobile ? 2 : 4,
            width: isMobile ? '90%' : '400px',
            borderRadius: 2,
          }}
        >
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={handleCloseResultModal}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            {`Intento ${currentTrialIndex !== null ? currentTrialIndex + 1 : ''} en altura ${
              selectedHeight !== null ? selectedHeight.value.toFixed(2) : ''
            } m para ${selectedParticipant ? selectedParticipant.fullName : ''}`}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            {['X', '-', 'O', 'R'].map((result, index) => (
              <Button
                key={index}
                variant="contained"
                sx={{ 
                  margin: isMobile ? '2px' : '5px',
                  ...responsiveStyles.actionButtons
                }}
                color={
                  result === 'X'
                    ? 'error'
                    : result === 'O'
                    ? 'success'
                    : result === 'R'
                    ? 'info'
                    : 'warning'
                }
                onClick={() => {
                  handleTrialResult(result);
                }}
              >
                {result}
              </Button>
            ))}
          </Box>
          {selectedParticipant && selectedHeight && 
             participantsResults[selectedParticipant.idNumber]?.heights?.[selectedHeight.value.toFixed(2)]?.attempts?.[currentTrialIndex] && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteResult}
                >
                  {isMobile ? "Eliminar" : "Eliminar Resultado"}
                </Button>
              </Box>
            )}
        </Box>
      </Modal>

      {/* Modal para marca de siembra */}
      <Modal open={openSeedMarkModal} onClose={() => {
        setOpenSeedMarkModal(false);
        setSelectedSeedParticipant(null);
      }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: isMobile ? 2 : 4,
            width: isMobile ? '85%' : '300px',
            borderRadius: 2,
          }}
        >
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => {
              setOpenSeedMarkModal(false);
              setSelectedSeedParticipant(null);
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            Marca de Siembra
          </Typography>
          <TextField
            fullWidth
            label="Marca de Siembra"
            value={selectedSeedParticipant ? seedMarks[selectedSeedParticipant.idNumber] || '' : ''}
            onChange={(e) => {
              if (selectedSeedParticipant) {
                setSeedMarks({
                  ...seedMarks,
                  [selectedSeedParticipant.idNumber]: e.target.value
                });
              }
            }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => {
                if (selectedSeedParticipant) {
                  handleSaveSeed(selectedSeedParticipant.idNumber, seedMarks[selectedSeedParticipant.idNumber] || '');
                }
              }}
            >
              Guardar
            </Button>
            <Button 
              variant="outlined" 
              color="error" 
              onClick={() => {
                if (selectedSeedParticipant) {
                  handleDeleteSeedMark(selectedSeedParticipant.idNumber);
                }
              }}
            >
              Eliminar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal para desempate */}
      {openTiebreakModal && (
        <Modal open={openTiebreakModal} onClose={handleCancelTiebreak}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: isMobile ? 2 : 4,
              width: responsiveStyles.modalWidth,
              borderRadius: 2,
            }}
          >
            <IconButton
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={handleCancelTiebreak}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" gutterBottom>
              Desempate
            </Typography>
            <Typography variant="body1" gutterBottom>
              {`Hay un empate en primer lugar en la altura ${tiebreakHeight ? tiebreakHeight.toFixed(2) : 'NM'} m.`}
            </Typography>
            <Typography variant="body1" gutterBottom>
              Participantes en empate:
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: '200px', mb: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={responsiveStyles.tableCell}>Número</TableCell>
                    <TableCell align="center" sx={responsiveStyles.tableCell}>Nombre y Equipo</TableCell>
                    <TableCell align="center" sx={responsiveStyles.tableCell}>Intentos Fallidos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(tiebreakParticipants || []).map((id) => {
                    const participant = (heatDetails.participants || []).find(p => p.idNumber === id);
                    const totalFouls = getTotalFailedAttempts(id);
                    return (
                      <TableRow key={id}>
                        <TableCell align="center" sx={responsiveStyles.tableCell}>{participant?.idNumber || '-'}</TableCell>
                        <TableCell align="center" sx={responsiveStyles.tableCell}>
                          <Typography sx={{ fontSize: responsiveStyles.typography.nameSize }}>{participant?.fullName || '-'}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: responsiveStyles.typography.teamSize }}>
                            {participant?.affiliation || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={responsiveStyles.tableCell}>{totalFouls}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Campo para agregar la siguiente altura */}
            <Typography variant="body1" gutterBottom>
              {`Ingrese la siguiente altura para desempate:`}
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Nueva Altura (m)"
              value={tiebreakHeight !== null ? tiebreakHeight.toFixed(2) : ''}
              onChange={(e) => setTiebreakHeight(parseFloat(e.target.value) || null)}
              sx={{ mb: 2 }}
              inputProps={{ step: 0.01, min: 0 }}
            />
            {/* Botones para Agregar o Mantener Empate */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="contained" color="primary" onClick={() => handleDesempateOption('Agregar', tiebreakHeight)}>
                Agregar
              </Button>
              <Button variant="outlined" color="secondary" onClick={() => handleDesempateOption('Mantener Empate')}>
                {isMobile ? "Mantener" : "Mantener Empate"}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      {/* Snackbars para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VerticalEvents;